import { groupBy, sumBy } from 'lodash-es'

import { getMappedColors } from '../colorsMap'
import { getAxisLabelFormatter } from '../formatChartData'
import { ChartData } from '@/Atomic/Organisms/Charts/acBar/dataConverter'
import { getCurrentLocale } from '@/utils/localeMapping'

interface RadialBarLabelFormatterOpts {
  seriesIndex?: number
}

export function getRadialChartOptionsAndData(
  data: ChartData,
  showLegend: boolean,
) {
  try {
    const { metaData, chartData = [] } = data

    // Group data by group index (each group represents an entry)
    const groupedData = groupBy(chartData, 'group')

    // Extract entries (not sum)
    const allEntries = Object.keys(groupedData)
      .map((groupKey) => {
        const group = groupedData[groupKey]
        if (!group) return null

        const groupIndex = parseInt(groupKey)
        const name = metaData.properties.groupTitles?.[groupIndex] || ''

        // x=0 is value, x=1 is target, x=2 is count
        const valuePoint = group.find((p) => p.x === 0)
        const countPoint = group.find((p) => p.x === 2)

        return {
          name,
          value: valuePoint?.value || 0,
          count: countPoint?.value || 0,
          color: valuePoint?.color || 0,
          type: valuePoint?.type || 'entry',
        }
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null)

    const entries = allEntries.filter((entry) => entry.type === 'entry')

    const names = entries.map((e) => e.name)
    const values = entries.map((e) => e.value)
    const counts = entries.map((e) => e.count)

    const colors = getMappedColors(
      entries.map((e) => metaData.colors?.[e.color] || ''),
    )

    // Calculate sum
    const sumValue = sumBy(entries, 'value')
    const sumCount = sumBy(entries, 'count')

    // Get locale with fallback for Storybook/test environments
    const locale = getCurrentLocale()
    const formatterY = getAxisLabelFormatter(
      metaData.properties?.yFormat,
      locale,
      (n: number) =>
        n.toLocaleString(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }) + ' €',
    )
    const formatterX = getAxisLabelFormatter(
      metaData.properties?.xFormat,
      locale,
      (n: number) =>
        n.toLocaleString(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }),
    )

    // Calculate percentages for radial display
    const max = Math.max(...counts)
    const n = 100 / max

    const realCounts: number[] = []
    const newCounts = counts.map((num, index) => {
      realCounts[index] = num
      return Math.floor(num * n) + index / 100
    })

    function getRealNumber(num: number) {
      const numStr = num.toString()
      const idx =
        numStr.indexOf('.') > -1
          ? parseInt(numStr.substring(numStr.indexOf('.') + 1))
          : 0
      return (
        formatterX(realCounts[idx] || 0) + '/' + formatterY(values[idx] || 0)
      )
    }

    const options = {
      series: newCounts,
      chart: {
        type: 'radialBar',
      },
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: '30%',
            background: 'transparent',
            image: undefined,
          },
          dataLabels: {
            name: {
              fontSize: '22px',
              show: true,
            },
            value: {
              fontSize: '16px',
              show: true,
              formatter: function (val: number) {
                return getRealNumber(val)
              },
            },
            total: {
              show: true,
              label: 'Total',
              formatter: function () {
                return formatterX(sumCount) + '/' + formatterY(sumValue)
              },
            },
          },
          barLabels: {
            enabled: true,
            useSeriesColors: true,
            offsetX: -8,
            fontSize: '12px',
            formatter: function (
              seriesName: string,
              opts: RadialBarLabelFormatterOpts,
            ) {
              return (
                seriesName +
                ': ' +
                formatterX(
                  typeof opts.seriesIndex === 'number'
                    ? counts[opts.seriesIndex] || 0
                    : 0,
                )
              )
            },
          },
        },
      },
      colors: colors,
      labels: names,
      responsive: [
        {
          options: {
            legend: {
              show: showLegend,
            },
          },
        },
      ],
    }

    return { options: options, data: newCounts }
  } catch (error) {
    console.error(
      'Error processing radial chart data:',
      error instanceof Error ? error.message : 'Unknown error',
    )

    const fallbackOptions = {
      series: [],
      chart: {
        type: 'radialBar',
      },
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: '30%',
            background: 'transparent',
          },
          dataLabels: {
            name: {
              fontSize: '22px',
              show: true,
            },
            value: {
              fontSize: '16px',
              show: true,
            },
            total: {
              show: true,
              label: 'Total',
            },
          },
        },
      },
      colors: [],
      labels: [],
      responsive: [
        {
          options: {
            legend: {
              show: false,
            },
          },
        },
      ],
    }

    return { options: fallbackOptions, data: [] }
  }
}
